import React from "react";

// A deploy replaces the container, so the previous build's hashed chunks stop
// existing and a long-lived tab breaks on its next lazy route. Re-importing the
// same URL cannot help: the browser records the failure in its module map, so a
// second import() of that specifier never reaches the network. Only a fresh
// index.html knows the new chunk names.

const RELOAD_MARKER_KEY = "chunk-reload-at";
const RELOAD_COOLDOWN_MS = 10_000;
const RELOAD_GRACE_MS = 5_000;
const PROBE_TIMEOUT_MS = 2_000;

// Wording differs per browser, and a missing chunk that `serve -s` rewrites to
// index.html surfaces as a MIME error instead.
export const isModuleLoadError = (error: unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);

  return /dynamically imported module|Importing a module script failed|Failed to fetch|Load failed|MIME type|ChunkLoadError/i.test(message);
};

// Reloading into a container that is still restarting swaps the app for the
// browser's own error page, so only commit to it once the server answers.
const isServerReachable = async (): Promise<boolean> => {
  try {
    const response = await fetch(import.meta.env.BASE_URL, {
      cache : "no-store",
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    });

    return response.ok;
  } catch {
    return false;
  }
};

// Cooldown keeps a build that is genuinely broken from reloading forever.
const reloadOnce = (): boolean => {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_MARKER_KEY) ?? 0);

    if (Date.now() - last < RELOAD_COOLDOWN_MS) {
      return false;
    }

    window.sessionStorage.setItem(RELOAD_MARKER_KEY, String(Date.now()));
  } catch {
    return false;
  }

  window.location.reload();

  return true;
};

// The document normally unloads long before this rejects; the delay only runs
// out when the reload was cancelled, e.g. a beforeunload prompt the user
// dismissed. Settling then is what stops the tab hanging on the spinner.
const untilUnload = <T,>(error: unknown): Promise<T> =>
  new Promise((_, reject) => { setTimeout(() => reject(error), RELOAD_GRACE_MS); });

export const importWithReload = async <T,>(factory: () => Promise<T>): Promise<T> => {
  try {
    return await factory();
  } catch (error) {
    if (!isModuleLoadError(error)) {
      throw error;
    }

    if (await isServerReachable() && reloadOnce()) {
      return untilUnload<T>(error);
    }

    // AppErrorScreen reads the original error to offer a manual reload.
    throw error;
  }
};

export const lazyWithReload: typeof React.lazy = factory =>
  React.lazy(() => importWithReload(factory));
